<script lang="ts">
    import { fly } from 'svelte/transition'
    import { onMount, tick } from 'svelte'
    import dayjs from 'dayjs'
    import { GridButton, GridClass, GridColAlignEnum, GridItemShow, GridColTypeEnum, GridSlot, GridCol, GridLine } from './zgrid'
    import Fa from 'svelte-fa'
    import { faShareAlt, faFileExport, faFilter, faPrint, faSortAlphaDown, faSortAlphaUpAlt, faTimes, faTrash, faBolt } from '@fortawesome/free-solid-svg-icons'
    import type { SharePageEntity } from '../../../modules/toolsApi/share-pages/share-page.entity'
    import Popover from '../popover.svelte'
    import SharePageLink from '../share-page-link.svelte'
    import ZgridLine from './zgrid_line.svelte'
    import Zbutton from './zbutton.svelte'
    import Zinput from './zinput.svelte'
    import Zgrid2 from './zgrid2.svelte'

    // ################### RECURSOS OBRIGATÓRIOS:
    // SELECIONAR TUDO / GERAR PÚBLICO ESTÁTICO / EXPORTAR / IMPRIMIR / PAGINAÇÃO "CARREGAR MAIS" // botão "pessoa"
    export let grid: GridClass
    export let shared: boolean = false
    export { className as class }
    let className = ''

    // O Zgrid2 é o grid do app. Este componente existe só para as telas que ainda o chamam pelo nome antigo:
    // ele repassa o mesmo GridClass para o Zgrid2, sempre — antes isso dependia da flag "grid" = 2, que saiu
    // de cena. Todo o corpo abaixo do {#if useGrid2} é o grid velho, que não é mais desenhado por ninguém.
    const useGrid2 = true

    let loaded = false
    const utils = window.api.utils.utils
    const isMobile = screen.width <= 900
    // seleção em massa (col.type = checkbox) só para usuário super. optional chaining de propósito:
    // o zgrid também é montado em página compartilhada/impressão, onde pode não haver login/token.
    const canBulkSelect = !!window.store?.token?.permissions?.super
    let conditionalFly
    const flyParams = isMobile ? { x: 0, y: 250 } : { x: 250, y: 0 }
    onMount(async () => {
        loaded = true
    })

    let lastUpdate = null
    let lastTotalItems = 0
    // com o Zgrid2 no ar este componente não renderiza nada, então nem calcula (ordenação, totais e afins são lá)
    $: if (!useGrid2) {
        if (grid && grid.lines.length > 100) {
            conditionalFly = () => {}
        } else {
            conditionalFly = fly
        }
        if (grid && grid._updatedAt !== lastUpdate) {
            haveTotals = false
            haveResumes = false
            tick().then(() => {
                if (lastTotalItems !== grid.lines.length) {
                    lineSelected = null
                    lastTotalItems = grid.lines.length
                }
                lastSort = -1
                sortDescending = false
                query = ''
                lastUpdate = grid._updatedAt
                haveTotals = grid.cols.some((el) => el.showTotal)
                haveResumes = grid.cols.some((el) => el.showTotalBox)
                grid = grid
            })
        }
    }

    let haveTotals = false
    let haveResumes = false
    let sortDescending = false
    let lastSort = -1
    async function sort(index: number) {
        if (index < 0) return
        const type = grid.cols[index].type
        if (type === GridColTypeEnum.button || type === GridColTypeEnum.checkbox) {
            return
        }
        if (lastSort !== index) {
            sortDescending = false
        }
        if (type === GridColTypeEnum.date || type === GridColTypeEnum.datetime) {
            sortBy_date(index, sortDescending)
        } else if (type === GridColTypeEnum.time || type === GridColTypeEnum.time_short) {
            sortBy_time(index, sortDescending)
        } else if (type === GridColTypeEnum.float || type === GridColTypeEnum.float_2 || type === GridColTypeEnum.int || type === GridColTypeEnum.percent) {
            sortBy_number(index, sortDescending)
        } else {
            sortBy_string(index, sortDescending)
        }
        grid = grid
        lastSort = index
        sortDescending = !sortDescending
    }

    function sortBy_string(index: number, descending: boolean) {
        grid.lines.sort((a: any, b: any) => {
            const labelA = a.slots[index].content?.label || a.slots[index].content || ''
            const labelB = b.slots[index].content?.label || b.slots[index].content || ''
            const result = labelA.localeCompare(labelB)
            return descending ? -result : result
        })
    }

    function sortBy_date(index: number, descending: boolean) {
        grid.lines.sort((a, b) => {
            let result = +new Date(a.slots[index].content) - +new Date(b.slots[index].content)
            return descending ? -result : result
        })
    }

    function sortBy_time(index: number, descending: boolean) {
        grid.lines.sort((a, b) => {
            const valueA = +new Date('2000-01-01 ' + dayjs(a.slots[index].content).format('HH:mm:ss'))
            const valueB = +new Date('2000-01-01 ' + dayjs(b.slots[index].content).format('HH:mm:ss'))
            let result = valueA - valueB
            return descending ? -result : result
        })
    }

    function sortBy_number(index: number, descending: boolean) {
        grid.lines.sort((a, b) => {
            let numberA = a.slots[index].content === grid.defaultEmptyValue ? 0 : parseInt(a.slots[index]?.content?.toString().replace('%', ''))
            let numberB = b.slots[index].content === grid.defaultEmptyValue ? 0 : parseInt(b.slots[index]?.content?.toString().replace('%', ''))
            return !descending ? numberA - numberB : numberB - numberA
        })
    }

    function evaluateContent(col: GridCol, slot: GridSlot) {
        if (col.type === GridColTypeEnum.date) {
            if (!slot.content) return grid.defaultEmptyValue
            if (isMobile) return dayjs(slot.content).format('DD/MM/YY')
            return dayjs(slot.content).format('DD/MM/YYYY')
        }
        if (col.type === GridColTypeEnum.time) {
            if (!slot.content) return grid.defaultEmptyValue
            return dayjs(slot.content).format('HH:mm:ss')
        }
        if (col.type === GridColTypeEnum.time_short) {
            if (!slot.content) return grid.defaultEmptyValue
            return dayjs(slot.content).format('HH:mm')
        }
        if (col.type === GridColTypeEnum.datetime) {
            if (!slot.content) return grid.defaultEmptyValue
            if (isMobile) return dayjs(slot.content).format('DD/MM/YY HH:mm')
            return dayjs(slot.content).format('DD/MM/YYYY HH:mm:ss')
        }
        if (col.type === GridColTypeEnum.datetime_friendly) {
            if (!slot.content) return grid.defaultEmptyValue
            return window.api.utils.utils.dateFriendly(slot.content)
            //return friendlyDate(slot.content)
        }
        if (col.type === GridColTypeEnum.string) {
            if (!slot.content) return grid.defaultEmptyValue
            if (col.maxLength > 0 && slot.content.length > col.maxLength) {
                return String(slot.content).substring(0, col.maxLength - 3) + '...'
            }
            return slot.content
        }
        if (col.type === GridColTypeEnum.int) {
            if (slot.content === null || slot.content === undefined) return grid.defaultEmptyValue
            return slot.content.toString()
        }
        if (col.type === GridColTypeEnum.float) {
            if (slot.content === null || slot.content === undefined) return grid.defaultEmptyValue
            return slot.content.toString()
        }
        if (col.type === GridColTypeEnum.percent) {
            if (slot.content === null || slot.content === undefined) return grid.defaultEmptyValue
            if (slot.content > 1000) return '1000%+'
            return parseInt(slot.content).toString() + '%'
        }
        if (col.type === GridColTypeEnum.float_2) {
            if (slot.content === null || slot.content === undefined) return grid.defaultEmptyValue
            return slot.content.toFixed(2)
        }
        if (col.type === GridColTypeEnum.phone) {
            if (slot.content === null || slot.content === undefined) return grid.defaultEmptyValue
            return utils.phoneFriendly(slot.content)
        }
        if (col.type === GridColTypeEnum.email) {
            if (!slot.content) return grid.defaultEmptyValue
            return slot.content.toString().toLowerCase().trim()
        }
        return slot.content.toString()
    }

    async function printGrid() {
        const me = (await import(`./zgrid.svelte`)).default
        window.printer({
            component: [me],
            props: [{ grid: withoutBulkSelection(grid) }],
        })
        //window.print()
    }

    function download(filename, text) {
        var bom = '\uFEFF' // BOM UTF-8
        var pom = document.createElement('a')
        pom.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(bom + text))
        pom.setAttribute('download', filename)

        if (document.createEvent) {
            var event = document.createEvent('MouseEvents')
            event.initEvent('click', true, true)
            pom.dispatchEvent(event)
        } else {
            pom.click()
        }
    }
    function exportGrid() {
        let csv = ''

        for (const col of grid.cols) {
            csv += col.content.toString().replaceAll(';', ',').replaceAll('"', ' ') + ';'
        }
        for (const line of grid.lines) {
            if (line.selected && line.visible) {
                if (!evaluateFilter(line)) continue
                csv += '\n'
                let index = 0
                for (const slot of line.slots) {
                    const type: GridColTypeEnum = grid.cols[index].type
                    let content = ''
                    if (type === GridColTypeEnum.date) {
                        content = dayjs(slot.content).format('DD/MM/YYYY')
                    } else if (type === GridColTypeEnum.datetime || type === GridColTypeEnum.datetime_friendly) {
                        content = dayjs(slot.content).format('DD/MM/YYYY HH:mm:ss')
                    } else if (type === GridColTypeEnum.float_2) {
                        content = (Math.round(parseFloat(slot.content) * 100) / 100).toString().replaceAll('.', ',')
                    } else if (type === GridColTypeEnum.percent) {
                        content = parseInt(slot.content.toString().replaceAll('.', ',')) + '%'
                    } else if (type === GridColTypeEnum.time) {
                        content = dayjs(slot.content).format('HH:mm:ss')
                    } else if (type === GridColTypeEnum.time_short) {
                        content = dayjs(slot.content).format('HH:mm')
                    } else if (type === GridColTypeEnum.phone) {
                        content = utils.phoneFriendly(slot.content)
                    } else if (typeof slot.content === 'object') {
                        console.log('other_type', GridColTypeEnum[type], slot)
                        content = ''
                    } else {
                        content = slot.content
                    }
                    if (!content || content === grid.defaultEmptyValue) content = ''
                    content = content.toString()
                    if (content.includes(',')) {
                        csv += '"' + content.toString().replaceAll(';', ',').replaceAll('"', ' ') + '";'
                    } else {
                        csv += content.toString().replaceAll(';', ',').replaceAll('"', ' ') + ';'
                    }
                    index++
                }
            }
        }
        download(grid.title + '_' + grid.subtitle + '.csv', csv)
    }

    let query: string = ''
    function htmlFilter(termo) {
        termo = termo
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase()
        for (let line of grid.lines) {
            for (let slot of line.slots) {
                let content = ''
                let slotContents = Array.isArray(slot.content) ? slot.content : [slot.content]
                for (const slotContent of slotContents) {
                    if (!slotContent) continue
                    if (typeof slotContent === 'object') {
                        content += (slotContent.content || slotContent.label) + ' '
                        continue
                    }
                    content += slotContent + ' '
                }
                if (!content) continue
                if (
                    content
                        .toString()
                        .normalize('NFD')
                        .replace(/[\u0300-\u036f]/g, '')
                        .toLowerCase()
                        .indexOf(termo) >= 0
                ) {
                    line.visible = true
                    break
                } else {
                    line.visible = false
                }
            }
        }
        grid = grid
    }

    let delay = null
    async function search(query) {
        if (delay) clearInterval(delay)
        delay = setTimeout(() => {
            htmlFilter(query)
        }, 500)
    }

    function getTotal(index) {
        let total = 0
        for (const line of grid.lines) {
            if (!evaluateFilter(line)) continue
            if (line.visible && line.removed != true && line.slots[index].content) {
                total += line.slots[index].content
            }
        }
        if (grid.cols[index].type === GridColTypeEnum.float_2) {
            return total.toFixed(2)
        }
        return total
    }

    let lineSelected = null
    function lineClicked(index: number) {
        if (lineSelected === index) {
            lineSelected = null
            return
        }
        lineSelected = index
    }

    let filterOptions: { label: string; value: string }[] = []
    let filterIndex: number = -1
    let popupFilter: HTMLElement
    async function filter(event: MouseEvent, index: number) {
        filterOptions = []
        for (const line of grid.lines) {
            const slot = line.slots[index]
            if (Array.isArray(slot.content)) {
                for (const item of slot.content) {
                    if (item.label && !filterOptions.some((el) => el.value.toString() === item.label.toString())) {
                        filterOptions.push(makeFilterOption(grid.cols[index], slot, item.label.toString())) ////////////
                    }
                }
                continue
            }
            if (!Array.isArray(slot.content)) {
                const content = (slot.content?.label || slot.content)?.toString()
                if (content && !filterOptions.some((el) => el.value.toString() === content)) {
                    filterOptions.push(makeFilterOption(grid.cols[index], slot, content)) //////////////
                }
            }
            continue
        }
        filterIndex = index
        await tick()
        let left = event.clientX + 20
        if (left + 420 > window.innerWidth) left = left - 430
        let top = event.clientY
        if (top + popupFilter.offsetHeight > window.innerHeight) top = window.innerHeight - popupFilter.offsetHeight - 20
        popupFilter.style.top = top + 'px'
        popupFilter.style.left = left + 'px'
        filterOptions.sort((a, b) => a.label.localeCompare(b.label))
        filterOptions = filterOptions
    }

    function makeFilterOption(col: GridCol, slot: GridSlot, content: string) {
        let value = content
        let label = ''
        if (
            col.type === GridColTypeEnum.badge ||
            col.type === GridColTypeEnum.button ||
            col.type === GridColTypeEnum.toggle ||
            col.type === GridColTypeEnum.zinput
        ) {
            label = content
        } else {
            label = evaluateContent(col, slot)
        }
        return { label, value }
    }

    async function toggleFilter(content: string) {
        const index = grid.cols[filterIndex].filter.findIndex((el) => el === content)
        if (index < 0) {
            grid.cols[filterIndex].filter.push(content)
        } else {
            grid.cols[filterIndex].filter.splice(index, 1)
        }
        redrawGrid()
    }

    function clearFilter() {
        grid.cols[filterIndex].filter = []
        filterIndex = -1
        redrawGrid()
    }

    async function redrawGrid() {
        let oldREsumes = haveResumes
        haveResumes = false
        let oldTotals = haveTotals
        haveTotals = false
        await tick()
        grid = grid
        haveTotals = oldTotals
        haveResumes = oldREsumes
    }

    // ###################################################### seleção em massa (col.type = checkbox)
    // "selecionar tudo" e as ações em massa consideram só as linhas atualmente visíveis (respeitando
    // busca/filtro em vigor) — mesmo critério que exportGrid() já usa pro CSV, pra não marcar/agir
    // silenciosamente sobre linhas que o usuário filtrou pra fora de vista.
    function visibleLines(): GridLine[] {
        return grid.lines.filter((line) => line.visible && evaluateFilter(line))
    }

    function allVisibleChecked(): boolean {
        const visible = visibleLines()
        return visible.length > 0 && visible.every((line) => line.checked)
    }

    // usado só no template (checked={headerAllChecked}, não checked={allVisibleChecked()}): uma
    // chamada de função direta no template não é reativa no Svelte — a análise de dependência olha
    // só os identificadores escritos na própria expressão ("allVisibleChecked", uma função que nunca
    // muda), então o valor nunca seria reavaliado depois da montagem inicial, mesmo com "grid = grid"
    // em outro lugar. "$: ... = fn(grid)" referencia "grid" de propósito, que o Svelte já rastreia.
    $: headerAllChecked = grid ? allVisibleChecked() : false

    function toggleSelectAll() {
        const shouldCheck = !allVisibleChecked()
        for (const line of visibleLines()) {
            line.checked = shouldCheck
        }
        grid = grid
    }

    function checkedValues(col_index: number): any[] {
        return visibleLines()
            .filter((line) => line.checked)
            .map((line) => line.slots[col_index].content)
    }

    let bulkMenuColIndex: number = -1
    let popupBulkMenu: HTMLElement
    async function openBulkMenu(event: MouseEvent, index: number) {
        bulkMenuColIndex = index
        await tick()
        let left = event.clientX + 20
        if (left + 420 > window.innerWidth) left = left - 430
        let top = event.clientY
        if (top + popupBulkMenu.offsetHeight > window.innerHeight) top = window.innerHeight - popupBulkMenu.offsetHeight - 20
        popupBulkMenu.style.top = top + 'px'
        popupBulkMenu.style.left = left + 'px'
    }

    function closeBulkMenu() {
        bulkMenuColIndex = -1
    }

    function runBulkAction(button: GridButton) {
        if (button.disabled) return
        const values = checkedValues(bulkMenuColIndex)
        closeBulkMenu()
        button.callback?.(values)
    }

    function evaluateShowColumn(col: GridCol) {
        if (col.isGroup) return false
        if (!col.show) return false
        if (col.type === GridColTypeEnum.checkbox && !canBulkSelect) return false
        if (shared) {
            if (col.type === GridColTypeEnum.button) return false
            if (col.type === GridColTypeEnum.toggle) return false
            if (col.type === GridColTypeEnum.zinput) return false
            if (col.type === GridColTypeEnum.checkbox) return false
        }
        // seleção em massa é só desktop — o cabeçalho com o ícone de raio (que abre o menu de ações)
        // não existe no layout mobile (cards empilhados, sem essa linha de cabeçalho)
        if (isMobile && col.type === GridColTypeEnum.checkbox) return false
        if (isMobile && col.mobile === GridItemShow.hide) return false
        return true
    }

    function evaluateFilter(line: GridLine) {
        for (let index = 0; index < grid.cols.length; index++) {
            const col = grid.cols[index]
            if (col.filter.length === 0) continue
            if (Array.isArray(line.slots[index].content)) {
                let found = line.slots[index].content.some((el) => col.filter.includes(el.label.toString()))
                if (!found) return false
            } else {
                const content = (line.slots[index].content?.label || line.slots[index].content)?.toString()
                if (!col.filter.includes(content)) return false
            }
        }
        return true
    }

    function checkAllowFilter(type: GridColTypeEnum) {
        if (type === GridColTypeEnum.toggle) return false
        if (type === GridColTypeEnum.zinput) return false
        if (type === GridColTypeEnum.checkbox) return false
        if (isMobile) return false
        return true
    }

    function getRandom(min, max) {
        return Math.round(Math.random() * (max - min) + min)
    }

    // cópia do grid sem as colunas de seleção em massa (e o slot correspondente de cada linha) — usada
    // em compartilhamento e impressão. não basta esconder na renderização: o "content" dessas colunas
    // costuma ser a chave primária do registro, e não deve ir junto no payload do link público.
    function withoutBulkSelection(source: GridClass): GridClass {
        const keep = source.cols.map((col) => col.type !== GridColTypeEnum.checkbox)
        if (keep.every(Boolean)) return source
        return new GridClass({
            ...source,
            cols: source.cols.filter((_, index) => keep[index]),
            lines: source.lines.map((line) => new GridLine({ ...line, checked: false, slots: line.slots.filter((_, index) => keep[index]) })),
        })
    }

    let shareToken: string = null
    async function shareGrid() {
        const dto: Partial<SharePageEntity> = {
            data: [{ type: 'zgrid', data: { grid: withoutBulkSelection(grid), shared: true } }],
        }
        const result = await window.api.tools.sharePages.create(dto)
        shareToken = result.sharePageId + '_' + result.token
    }

    let lastContent: any = null
    function isNewGroup(line: GridLine) {
        const gIndex = grid.cols.findIndex((el) => el.isGroup)
        if (gIndex < 0) return null
        const content = line.slots[gIndex].content
        if (lastContent == JSON.stringify(content)) return null
        lastContent = JSON.stringify(content)
        return content
    }
</script>

{#if shareToken}
    <Popover on:close={() => (shareToken = null)} width="780px" title="Link de compartilhamento">
        <SharePageLink token={shareToken}></SharePageLink>
    </Popover>
{/if}
{#if useGrid2}
    <!-- sempre: mesma configuração, grid novo. o ramo abaixo é o grid velho, que ninguém mais alcança -->
    <Zgrid2 {grid} {shared} class={className} />
{:else if grid && loaded}
    <div class="zgrid zgrid_mobile_{isMobile} {className}" class:zgrid_shared={shared}>
        {#if grid.showHeaders}
            <div class="zgrid_head">
                <div class="zgrid_head_info">
                    <div class="zgrid_title">
                        {grid.title || ''}
                    </div>
                    {#if grid.subtitle}
                        <div class="zgrid_headline">
                            {grid.subtitle}
                        </div>
                    {/if}
                </div>
                <div class="spacer" />
                {#if !shared && (grid.showActs || grid.acts?.length > 0)}
                    <div class="zgrid_head_act h-print">
                        {#if !isMobile && grid.showActs}
                            <Zinput
                                class="head_act_search"
                                type="text"
                                label="Buscar nos dados carregados"
                                bind:value={query}
                                on:keyup={() => search(query)}
                                on:change={() => search(query)}
                                on:clear={() => search('')}
                                showClear={true}
                            ></Zinput>
                            <Zbutton icon={faPrint} on:click={printGrid}></Zbutton>
                            <Zbutton icon={faShareAlt} on:click={shareGrid}></Zbutton>
                            <Zbutton icon={faFileExport} on:click={exportGrid}></Zbutton>
                        {/if}
                        {#each grid.acts as act}
                            <Zbutton
                                class={act.class}
                                icon={act.icon ? act.icon : null}
                                on:click={act.callback}
                                label={!isMobile || act.mobileShowLabel ? act.label : ''}
                                color={act.color}
                            ></Zbutton>
                        {/each}
                    </div>
                {/if}
            </div>
        {/if}
        <!-- ############################################################################ top box totals -->
        {#if haveResumes || grid.box?.length > 0}
            <div class="zgrid_resume">
                {#each grid.cols as col, col_index}
                    {#if col.showTotalBox}
                        <div class="zgrid_resume_box">
                            <div class="zgrid_resume_box_title">{col.content}</div>
                            <div class="zgrid_resume_box_value">{getTotal(col_index)}</div>
                            <div class="zgrid_resume_box_label"></div>
                        </div>
                    {/if}
                {/each}
                {#if grid.box}
                    {#each grid.box as box}
                        <div class="zgrid_resume_box {box.class}">
                            <div class="zgrid_resume_box_title">{box.label}</div>
                            <div class="zgrid_resume_box_value">{box.value.toFixed(box.decimals || 0)}</div>
                            <div class="zgrid_resume_box_label"></div>
                        </div>
                    {/each}
                {/if}
            </div>
        {/if}
        <!-- ###########################################################             -->

        {#if grid.cols.length > 0 && grid.lines.length === 0}
            <div class="text-muted text-center pt-3 pb-2 grid-empty">Nenhuma informação para exibir</div>
        {:else}
            <div class="zgrid_body zgrid_body-min-width-{!!grid.minWidth}" style={grid.fontSize ? 'font-size:' + grid.fontSize + ';' : ''}>
                <table class="zgrid_table" style="--zgrid-min-width:{grid.minWidth ? grid.minWidth : 'unset'};">
                    <!-- ########################################################################################################### head -->
                    {#if !isMobile && grid.showLabels}
                        <tr class="zgrid_caption_tr zgrid_caption">
                            {#each grid.cols as col, col_index (col_index)}
                                {#if evaluateShowColumn(col)}
                                    {#if !isMobile || col.mobile === GridItemShow.show}
                                        <td class="zgrid_caption_td" class:h-print={col.type === GridColTypeEnum.checkbox}>
                                            {#if col.type === GridColTypeEnum.checkbox}
                                                <!-- título vira o checkbox de selecionar/desselecionar tudo (o que está visível no
                                                     momento, respeitando busca/filtro), e o raio — no lugar de ordenar/filtrar,
                                                     que não fazem sentido aqui — abre o menu de bulkActions, igual ao popup de
                                                     filtro reaproveitado mais abaixo. -->
                                                <div class="zgrid_caption_td_content_outer type_caption_checkbox">
                                                    <span class="zgrid_caption_td_content">
                                                        <span class="zgrid_caption_td_content_text">
                                                            <input
                                                                class="form-check-input zgrid_checkbox"
                                                                type="checkbox"
                                                                checked={headerAllChecked}
                                                                on:change={toggleSelectAll}
                                                            />
                                                        </span>
                                                        {#if !shared && col.bulkActions?.length > 0}
                                                            <span
                                                                class="zgrid_caption_td_content_act zgrid_caption_td_content_bulk h-print"
                                                                on:click={(event) => openBulkMenu(event, col_index)}
                                                                title="Ações em massa"
                                                            >
                                                                <Fa icon={faBolt} />
                                                            </span>
                                                        {/if}
                                                    </span>
                                                </div>
                                            {:else if col.content}
                                                <div
                                                    class="zgrid_caption_td_content_outer type_caption_{GridColTypeEnum[col.type]} align_{GridColAlignEnum[
                                                        col.align
                                                    ]}"
                                                >
                                                    <span class="zgrid_caption_td_content">
                                                        <span class="zgrid_caption_td_content_text">
                                                            {col.content}
                                                        </span>
                                                        {#if !shared && grid.showFilter}
                                                            <span
                                                                class="zgrid_caption_td_content_act zgrid_caption_td_content_sort h-print"
                                                                on:click={() => sort(col_index)}
                                                                class:sorted={lastSort === col_index}
                                                            >
                                                                {#if sortDescending || !lastSort || lastSort !== col_index}
                                                                    <Fa icon={faSortAlphaDown} />
                                                                {:else}
                                                                    <Fa icon={faSortAlphaUpAlt} />
                                                                {/if}
                                                            </span>
                                                            {#if checkAllowFilter(col.type)}
                                                                <span
                                                                    class="zgrid_caption_td_content_act zgrid_caption_td_content_filter h-print"
                                                                    on:click={(event) => filter(event, col_index)}
                                                                    class:filtered={col.filter.length > 0}
                                                                >
                                                                    <Fa icon={faFilter}></Fa>
                                                                </span>
                                                            {/if}
                                                        {/if}
                                                    </span>
                                                </div>
                                            {/if}
                                        </td>
                                    {/if}
                                {/if}
                            {/each}
                        </tr>
                    {/if}

                    <!-- ########################################################################################################### lines -->
                    <!-- ########################################################################################################### lines -->

                    {#each grid.lines as line, lineIndex}
                        {@const group = isNewGroup(line)}
                        {#if group}
                            <tr class="is-group--true"><td colspan={grid.cols.length - 1}>{group}</td></tr>
                        {/if}
                        {#if line.visible && evaluateFilter(line)}
                            <tr
                                in:conditionalFly|global={{ x: -flyParams.x, y: -flyParams.y, duration: 200, delay: lineIndex * 5 }}
                                out:conditionalFly={{ x: flyParams.x, y: flyParams.y, duration: 200, delay: lineIndex * 5 }}
                                class="zgrid_tr zgrid_content {!grid.showLines ? 'zgrid_content-no-border' : ''} {line.class} line_removed_{line.removed}"
                                class:line_selected_true={lineIndex === lineSelected}
                                class:zgrid_tr_mobile={isMobile}
                                on:click={() => lineClicked(lineIndex)}
                            >
                                {#each line.slots as slot, slot_index}
                                    {@const col = grid.cols[slot_index]}
                                    {#if evaluateShowColumn(col)}
                                        {#if !isMobile}
                                            <td
                                                style={col.size ? 'width:' + (100 / 12) * col.size + '%;' : ''}
                                                class="zgrid_td type_{GridColTypeEnum[col.type]} align_{GridColAlignEnum[col.align]} {col.class} {slot.class}"
                                            >
                                                <ZgridLine
                                                    defaultEmptyString={grid.defaultEmptyValue}
                                                    {col}
                                                    {slot}
                                                    on:click={(ev) => lineClicked(ev.detail)}
                                                    {isMobile}
                                                    bind:checked={line.checked}
                                                    on:change={() => (grid = grid)}
                                                ></ZgridLine>
                                            </td>
                                        {:else}
                                            <div class="zgrid_td_mobile type_mobile_{GridColTypeEnum[col.type]} {slot.class}">
                                                {#if col.content}
                                                    <div class="zgrid_td_mobile_title">{col.content}</div>
                                                {/if}
                                                <ZgridLine
                                                    defaultEmptyString={grid.defaultEmptyValue}
                                                    {col}
                                                    {slot}
                                                    on:click={(ev) => lineClicked(ev.detail)}
                                                    {isMobile}
                                                ></ZgridLine>
                                            </div>
                                        {/if}
                                    {/if}
                                {/each}
                            </tr>
                        {/if}
                    {/each}

                    <!-- ############################################################################ totals -->
                    {#if !isMobile && haveTotals}
                        <tr class="zgrid_caption_tr zgrid_content">
                            {#each grid.cols as col, col_index}
                                {#if evaluateShowColumn(col)}
                                    <td
                                        style={col.size ? 'width:' + (100 / 12) * col.size + '%;' : ''}
                                        class="zgrid_caption_td type_{GridColTypeEnum[col.type]} align_{GridColAlignEnum[col.align]}"
                                    >
                                        {#if col.showTotal}
                                            <span class="zgrid_total">{getTotal(col_index)}</span>
                                        {/if}
                                    </td>
                                {/if}
                            {/each}
                        </tr>
                    {/if}
                    <!-- ############################################################################ -->
                </table>
            </div>
        {/if}

        {#if filterIndex >= 0}
            <div on:click={() => (filterIndex = -1)} class="zgrid_filter_outer"></div>
            <div class="zgrid_filter scroll-dark" bind:this={popupFilter}>
                <div class="zgrid_filter_title">
                    {#if grid.cols[filterIndex].filter.length > 0}
                        <div class="zgrid_filter_title_trash" on:click={clearFilter}>
                            <Fa icon={faTrash}></Fa> limpar
                        </div>
                    {/if}
                    <div class="spacer"></div>
                    <div class="zgrid_filter_title_close" on:click={() => (filterIndex = -1)}>
                        <Fa icon={faTimes}></Fa>
                    </div>
                </div>
                <div class="zgrid_filter_content scroll-dark">
                    {#each filterOptions as option}
                        <div
                            on:click={(event) => toggleFilter(option.value)}
                            class="zgrid_filter_content_item"
                            class:filtered={grid.cols[filterIndex].filter.includes(option.value)}
                        >
                            {option.label}
                        </div>
                    {/each}
                </div>
            </div>
        {/if}

        <!-- menu de ações em massa (col.type = checkbox, ícone de raio) — mesmo popup/posicionamento
             do filtro acima, reaproveitado de propósito em vez de duplicar a mecânica. -->
        {#if bulkMenuColIndex >= 0}
            <div on:click={closeBulkMenu} class="zgrid_filter_outer"></div>
            <div class="zgrid_filter scroll-dark" bind:this={popupBulkMenu}>
                <div class="zgrid_filter_title">
                    <div class="zgrid_filter_title_text">{checkedValues(bulkMenuColIndex).length} selecionado(s)</div>
                    <div class="spacer"></div>
                    <div class="zgrid_filter_title_close" on:click={closeBulkMenu}>
                        <Fa icon={faTimes}></Fa>
                    </div>
                </div>
                <div class="zgrid_filter_content scroll-dark">
                    {#if checkedValues(bulkMenuColIndex).length === 0}
                        <div class="zgrid_filter_content_item zgrid_filter_content_item-disabled">Nenhum item selecionado</div>
                    {:else}
                        {#each grid.cols[bulkMenuColIndex].bulkActions as action}
                            <div
                                class="zgrid_filter_content_item"
                                class:zgrid_filter_content_item-disabled={action.disabled}
                                title={action.title || ''}
                                on:click={() => runBulkAction(action)}
                            >
                                {#if action.icon}<Fa icon={action.icon} />{/if}
                                {action.label}
                            </div>
                        {/each}
                    {/if}
                </div>
            </div>
        {/if}
    </div>
{/if}

